import Modal, { ModalStateProps } from '../../../ui/Modal'
import { useContext, useState } from 'react'
import { ProjectContext } from '../../../contexts'
import api from '../../../api'
import './ImageGalleryModal.css'
import { Resource } from '../../../types'
import { Button, DataTable, Heading } from '../../../ui'
import { useTranslation } from 'react-i18next'
import ResourceFontModal from './ResourceFontModal'

interface ResourceModalProps extends ModalStateProps {
    resources: Resource[]
    setResources: (resources: Resource[]) => void
}

export default function ResourceModal({ open, onClose, resources, setResources }: ResourceModalProps) {
    const { t } = useTranslation()
    const [project] = useContext(ProjectContext)
    const [showFontCreate, setShowFontCreate] = useState(false)
    const [editFont, setEditFont] = useState<Resource>()

    const handleRemove = async (id: number) => {
        await api.resources.delete(project.id, id)
        setResources(resources.filter(resource => resource.id !== id))
    }

    const handleAddResource = (resource: Resource) => {
        setShowFontCreate(false)
        setResources([...resources, resource])
    }

    const handleUpdateResource = (resource: Resource) => {
        setEditFont(undefined)
        setResources(resources.map(item => item.id === resource.id ? resource : item))
    }

    return (
        <Modal
            title={t('config')}
            open={open}
            onClose={onClose}
            size="large">
            <Heading size="h4" title={t('fonts')} actions={
                <Button size="small" onClick={() => setShowFontCreate(true)}>{t('add_font')}</Button>
            } />
            <div className="resources">
                <DataTable
                    items={resources}
                    itemKey={({ item }) => item.id}
                    onSelectRow={item => setEditFont(item)}
                    columns={[
                        {
                            key: 'name',
                            title: t('name'),
                        },
                        {
                            key: 'family',
                            title: t('font_family'),
                            cell: ({ item }) => item.value.value,
                        },
                        {
                            key: 'url',
                            title: t('url'),
                            cell: ({ item }) => item.value.url,
                        },
                        {
                            key: 'weights',
                            title: t('font_weights'),
                            cell: ({ item }) => (item.value.weights ?? []).join(', ') || '—',
                        },
                        {
                            key: 'options',
                            title: t('options'),
                            cell: ({ item }) => (
                                <Button
                                    size="small"
                                    variant="destructive"
                                    onClick={async (e) => {
                                        e.stopPropagation()
                                        await handleRemove(item.id)
                                    }}>
                                    {t('delete')}
                                </Button>
                            ),
                        },
                    ]} />
            </div>

            <ResourceFontModal
                open={showFontCreate}
                onClose={() => setShowFontCreate(false)}
                onInsert={(resource) => handleAddResource(resource) }
            />

            <ResourceFontModal
                open={!!editFont}
                resource={editFont}
                onClose={() => setEditFont(undefined)}
                onInsert={(resource) => handleUpdateResource(resource) }
            />
        </Modal>
    )
}
